import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, GraduationCap, Wrench, Rocket, Sparkles, Building2, Package, Plug, BookOpen, Zap, ShieldCheck, ArrowRight, Loader2, Globe, Wand2, Gamepad2, NotebookPen, Hammer, Dna } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TIERS = [
  {
    id: "free",
    name: "Explorer",
    sub: "Free forever",
    icon: <Sparkles className="w-5 h-5" />,
    monthly_inr: 0, monthly_usd: 0, yearly_inr: 0, yearly_usd: 0,
    blurb: "All 2,560 chapters. All worlds. Forever free.",
    features: [
      "Unlimited access to every lesson, world & adventure",
      "Full 2,560-chapter library — searchable, tag-filtered",
      "Knowledge Graph, Constellation tours & Season Ribbons",
      "All 12 Core Adventures + 2 Epilogues (with certificates)",
      "5 AI prompts / month (apps + games)",
      "Community read + comment",
    ],
    cta: "Start free",
  },
  {
    id: "solo",
    name: "Solo",
    sub: "Hobbyist",
    icon: <Zap className="w-5 h-5" />,
    monthly_inr: 299, monthly_usd: 4, yearly_inr: 2699, yearly_usd: 36,
    blurb: "For tinkerers who want a steady drip of ideas.",
    features: [
      "15 prompts / month",
      "Unlimited refinements & branches",
      "Web preview + browser game preview",
      "Save up to 25 blueprints",
      "Public share preview links",
      "Email support",
    ],
    cta: "Choose Solo",
  },
  { id: "student", name: "Apprentice", sub: "Student", icon: <GraduationCap className="w-5 h-5" />,
    monthly_inr: 699, monthly_usd: 9, yearly_inr: 6299, yearly_usd: 79,
    blurb: "Turn curiosity into shipping skill.",
    features: [
      "30 prompts / month",
      "Everything free (all lessons + adventures + library)",
      "Save unlimited blueprints",
      "1 Swift → Canvas bridge / day",
      "Mini-projects: notes, habit tracker, finance",
      "Email support · 24h response",
    ],
    cta: "Choose Apprentice",
  },
  {
    id: "builder",
    name: "Creator",
    sub: "Indie Builder",
    icon: <Wrench className="w-5 h-5" />,
    monthly_inr: 1999, monthly_usd: 24, yearly_inr: 17999, yearly_usd: 219,
    blurb: "Idea → Xcode-ready starter, every week.",
    popular: true,
    features: [
      "250 prompts / month (overage at ₹8/prompt)",
      "Unlimited refinements & branches",
      "Full SwiftUI + SpriteKit / SceneKit / RealityKit code",
      "App Store kit + 5 AI marketing screenshots / month",
      "GitHub OAuth → 1-click repo push",
      "Swift → Canvas bridge unlimited",
      "TestFlight automation (`v*` tag → ship)",
      "Priority Claude access",
    ],
    cta: "Choose Creator",
  },
  {
    id: "studio",
    name: "Founder Mode",
    sub: "Studio",
    icon: <Rocket className="w-5 h-5" />,
    monthly_inr: 6999, monthly_usd: 84, yearly_inr: 62999, yearly_usd: 759,
    blurb: "Build real products, not just demos.",
    features: [
      "1,500 prompts / month (overage at ₹6/prompt)",
      "Everything in Creator",
      "Multi-app workspace + versioned blueprints",
      "30 marketing screenshots / month",
      "Team collaboration (3–10 seats)",
      "Investor pitch + product roadmap drafts",
      "Custom Emergent integrations",
      "White-glove onboarding · Slack support",
      "Early access to new features",
    ],
    cta: "Choose Founder Mode",
  },
];

const BUNDLES = [
  { id: "student-bundle", name: "Student Bundle", price_inr: 9999, blurb: "Apprentice (yearly) + full digital book library + 100 bonus prompts." },
  { id: "builder-bundle", name: "Builder Bundle", price_inr: 24999, blurb: "Creator (yearly) + AI Mentor + Pro Export Pack + 500 bonus prompts.", popular: true },
  { id: "founder-bundle", name: "Founder Bundle", price_inr: 79999, blurb: "Founder Mode (yearly) + every add-on + App Launch Pack + dedicated success manager." },
];

const ADDONS = [
  { id: "prompt-booster", name: "Prompt Booster · 100 extra prompts", price: "₹499 one-time", blurb: "Top up your monthly cap without changing tiers. Stacks. Never expires." },
  { id: "pro-export", name: "Pro Export Pack", price: "₹1,299/mo", blurb: "Production-ready SwiftUI, signed builds, deployment checklist." },
  { id: "ai-mentor", name: "AI Mentor Mode", price: "₹1,799/mo", blurb: "24/7 coding assistant, debugging explanations, personalized path." },
  { id: "marketing-burst", name: "Marketing Burst · 50 AI screenshots", price: "₹999 one-time", blurb: "Generate 50 App Store marketing screenshots in any tier." },
  { id: "launch-pack", name: "App Launch Pack", price: "₹6,999 one-time", blurb: "App Store submission support, screenshots, ASO + launch checklist." },
];

const BOOK_PRICING = [
  { label: "Digital book", price: "₹399 – ₹1,099" },
  { label: "Digital bundle (5 books)", price: "₹2,499" },
  { label: "Full digital library", price: "₹5,999" },
  { label: "Physical pop-up book", price: "₹1,799 – ₹5,999" },
  { label: "Collector edition", price: "₹9,999+" },
  { label: "School kit (20–200 books)", price: "₹35,000 – ₹3,00,000" },
];

// Apps × Tier inclusion matrix.
// Order: Explorer, Solo, Apprentice, Creator, Founder
const MATRIX_ROWS = [
  { section: true, label: "APPS INCLUDED", values: ["", "", "", "", ""] },
  { icon: <Globe className="w-4 h-4" />, label: "Website Studio",  values: [true, true, true, true, true] },
  { icon: <Wand2 className="w-4 h-4" />, label: "Prompt Studio (Apple apps)", values: [true, true, true, true, true] },
  { icon: <Gamepad2 className="w-4 h-4" />, label: "Game Studio",  values: [true, true, true, true, true] },
  { icon: <NotebookPen className="w-4 h-4" />, label: "Knuth · Doc workbench",  values: [true, true, true, true, true] },
  { icon: <Hammer className="w-4 h-4" />, label: "Kailash · Linux release workbench",  values: [true, true, true, true, true] },
  { icon: <Dna className="w-4 h-4" />,    label: "Mendel · Biology workbench",         values: [true, true, true, true, true] },

  { section: true, label: "KNOWLEDGE (FREE FOR EVERYONE)", values: ["", "", "", "", ""] },
  { icon: <BookOpen className="w-4 h-4" />, label: "Full library (2,560 chapters)",   values: [true, true, true, true, true] },
  { icon: <BookOpen className="w-4 h-4" />, label: "All 12 worlds of computing",      values: [true, true, true, true, true] },
  { icon: <BookOpen className="w-4 h-4" />, label: "Knowledge Graph & Constellation", values: [true, true, true, true, true] },
  { icon: <BookOpen className="w-4 h-4" />, label: "All Adventures + certificates",   values: [true, true, true, true, true] },
  { icon: <BookOpen className="w-4 h-4" />, label: "Mendel · lessons + walkthroughs", values: [true, true, true, true, true] },

  { section: true, label: "USAGE LIMITS", values: ["", "", "", "", ""] },
  { label: "Prompts / month (apps + games + websites)", values: ["5", "15", "30", "250", "1,500"] },
  { label: "Refinements & branches", values: ["3", "unlimited", "unlimited", "unlimited", "unlimited"] },
  { label: "Kailash build configs / month",             values: ["3", "20", "50", "300", "2,000"] },
  { label: "Knuth doc generations / month",             values: ["3", "20", "50", "300", "2,000"] },
  { label: "Mendel · AI research chats / month",         values: ["5", "20", "50", "150", "unlimited"] },
  { label: "Mendel · FASTA/VCF imports / month",         values: ["1", "5", "10", "50", "unlimited"] },
  { label: "GitHub repo imports",                        values: [true, true, true, true, true] },
  { label: "Saved blueprints",                           values: ["3", "25", "unlimited", "unlimited", "unlimited"] },

  { section: true, label: "PREMIUM CAPABILITIES", values: ["", "", "", "", ""] },
  { label: "App Store marketing screenshots / month",   values: [false, false, false, "5", "30"] },
  { label: "Swift → Canvas bridge",                     values: [false, false, "1 / day", "unlimited", "unlimited"] },
  { label: "TestFlight + Xcode Cloud automation",       values: [false, false, false, true, true] },
  { label: "Vercel one-click deploy",                    values: [true, true, true, true, true] },
  { label: "Public share previews",                      values: [false, true, true, true, true] },
  { label: "Priority Claude access",                     values: [false, false, false, true, true] },
  { label: "Team seats",                                 values: ["1", "1", "1", "1", "3–10"] },
  { label: "Investor pitch drafts",                      values: [false, false, false, false, true] },

  { section: true, label: "SUPPORT", values: ["", "", "", "", ""] },
  { label: "Community",                                  values: ["read-only", true, true, true, true] },
  { label: "Email support",                              values: [false, true, "24h", true, "dedicated"] },
];

export default function Pricing() {
  const [yearly, setYearly] = useState(false);
  const [buying, setBuying] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const startCheckout = async (sku, label, recurring = false) => {
    if (!user) {
      toast.info("Sign in to complete your purchase");
      navigate(`/signup?next=/pricing`);
      return;
    }
    setBuying(sku);
    try {
      const endpoint = recurring ? "/paypal/subscriptions/create" : "/paypal/orders/create";
      const { data } = await api.post(endpoint, { sku });
      if (!data?.approve_url) throw new Error("No approval URL from PayPal");
      sessionStorage.setItem("dpa_pending_checkout", JSON.stringify({ sku, label, recurring, at: Date.now() }));
      window.location.assign(data.approve_url);
    } catch (e) {
      setBuying(null);
      const msg = e?.response?.data?.detail || e.message || "Could not start checkout";
      toast.error(typeof msg === "string" ? msg : "Could not start checkout");
    }
  };

  const fmt = (inr, usd) => {
    if (inr === 0) return { main: "Free", sub: "" };
    return {
      main: `₹${inr.toLocaleString("en-IN")}`,
      sub: yearly ? `${usd > 0 ? "$" + usd : ""} / year` : `${usd > 0 ? "$" + usd : ""} / month`,
    };
  };

  const monthlyEquivalent = (yearlyInr, monthlyInr) => {
    if (!yearly || !yearlyInr || !monthlyInr) return null;
    const eff = Math.round(yearlyInr / 12);
    const saved = Math.round((1 - eff / monthlyInr) * 100);
    return { eff, saved };
  };

  return (
    <div className="container-narrow py-12 md:py-20" data-testid="pricing-page">
      <div className="text-center mb-10">
        <div className="label-eyebrow mb-3">Knowledge is free forever · Pay only for AI creation power</div>
        <h1 className="font-heading text-4xl md:text-6xl font-semibold leading-tight">
          Learn free. <span className="italic text-marigold">Pay to build.</span>
        </h1>
        <p className="text-ink2 mt-4 max-w-2xl mx-auto">
          Every chapter, every world, every adventure — free for everyone, forever. Paid tiers unlock AI creation (prompts, code gen, exports, marketing). Pay yearly to save up to <span className="font-semibold text-ink">25%</span>. 14-day money-back on every paid plan.
        </p>

        <div className="inline-flex mt-8 p-1 rounded-full bg-paper border border-border" data-testid="billing-toggle">
          <button
            onClick={() => setYearly(false)}
            className={`px-5 py-2 rounded-full text-sm font-medium transition ${!yearly ? "bg-white shadow-sm text-ink" : "text-ink2"}`}
            data-testid="billing-monthly"
          >
            Monthly
          </button>
          <button
            onClick={() => setYearly(true)}
            className={`px-5 py-2 rounded-full text-sm font-medium transition inline-flex items-center gap-2 ${yearly ? "bg-white shadow-sm text-ink" : "text-ink2"}`}
            data-testid="billing-yearly"
          >
            Yearly
            <span className="text-[10px] font-bold text-marigold uppercase tracking-wider">Save up to 25%</span>
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {TIERS.map(t => {
          const inr = yearly ? t.yearly_inr : t.monthly_inr;
          const usd = yearly ? t.yearly_usd : t.monthly_usd;
          const p = fmt(inr, usd);
          const eq = monthlyEquivalent(t.yearly_inr, t.monthly_inr);
          return (
            <div
              key={t.id}
              className={`relative rounded-3xl p-6 border-2 transition-all bg-white flex flex-col ${t.popular ? "border-saffron shadow-[0_20px_50px_-15px_rgba(244,162,97,0.45)] -translate-y-2 lg:scale-[1.03]" : "border-border"}`}
              data-testid={`tier-${t.id}`}
            >
              {t.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-saffron border-2 border-ink text-xs font-bold text-ink uppercase tracking-wider whitespace-nowrap">
                  Most popular
                </div>
              )}
              <div className="w-10 h-10 rounded-xl bg-paper text-marigold flex items-center justify-center mb-4">{t.icon}</div>
              <div className="text-xs text-ink2 uppercase tracking-widest font-semibold mb-1">{t.sub}</div>
              <h3 className="font-heading text-2xl font-semibold mb-1">{t.name}</h3>
              <p className="text-sm text-ink2 italic mb-4 min-h-[2.5em]">{t.blurb}</p>
              <div className="mb-5">
                <div className="font-heading text-3xl md:text-4xl font-bold leading-none">{p.main}</div>
                {p.sub && <div className="text-xs text-ink2 mt-1">{p.sub}</div>}
                {eq && (
                  <div className="text-[11px] text-marigold font-semibold mt-1">
                    ≈ ₹{eq.eff.toLocaleString("en-IN")}/mo · save {eq.saved}%
                  </div>
                )}
              </div>
              <ul className="space-y-2 mb-6 flex-grow">
                {t.features.map((f, i) => (
                  <li key={i} className="flex gap-2 text-sm text-ink2">
                    <Check className="w-4 h-4 mt-0.5 text-teal flex-shrink-0" /><span>{f}</span>
                  </li>
                ))}
              </ul>
              {t.id === "free" ? (
                <Link
                  to="/signup"
                  className="block text-center w-full rounded-full px-5 py-3 font-semibold transition border-2 border-ink text-ink hover:bg-ink hover:text-cream"
                  data-testid={`tier-cta-${t.id}`}
                >
                  {t.cta}
                </Link>
              ) : (
                <>
                  <button
                    type="button"
                    disabled={buying === `tier_${t.id}_${yearly ? "yearly" : "monthly"}`}
                    onClick={() => startCheckout(`tier_${t.id}_${yearly ? "yearly" : "monthly"}`, `${t.name} · ${yearly ? "Yearly" : "Monthly"}`, true)}
                    className={`block text-center w-full rounded-full px-5 py-3 font-semibold transition inline-flex items-center justify-center gap-2 disabled:opacity-60 ${t.popular ? "bg-saffron text-ink hover:-translate-y-0.5 shadow-[0_6px_20px_-4px_rgba(244,162,97,0.5)]" : "border-2 border-ink text-ink hover:bg-ink hover:text-cream"}`}
                    data-testid={`tier-cta-${t.id}`}
                  >
                    {buying === `tier_${t.id}_${yearly ? "yearly" : "monthly"}` ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>
                    ) : (
                      <>Subscribe with PayPal</>
                    )}
                  </button>
                  <p className="text-[11px] text-ink2 mt-2 text-center">Auto-renews · cancel anytime from your dashboard</p>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Trust strip */}
      <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mb-16 text-sm text-ink2" data-testid="pricing-trust-strip">
        <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-teal" /> 14-day money-back guarantee</span>
        <span className="inline-flex items-center gap-2"><Zap className="w-4 h-4 text-marigold" /> Cancel anytime, prorated refunds</span>
        <span className="inline-flex items-center gap-2"><Sparkles className="w-4 h-4 text-marigold" /> Switch tiers in one tap</span>
      </div>

      {/* Apps-per-tier matrix */}
      <section className="mb-20" data-testid="apps-matrix">
        <div className="text-center mb-8">
          <div className="label-eyebrow mb-3 inline-flex items-center gap-2"><Package className="w-3.5 h-3.5" /> One subscription · every app</div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold">What you get in each tier.</h2>
          <p className="text-ink2 mt-3 max-w-2xl mx-auto text-sm">Knowledge is free for everyone — every lesson, world, and adventure. Paid tiers unlock more AI creation power: more prompts, more build configs, and premium capabilities.</p>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-border">
          <table className="w-full text-sm" data-testid="matrix-table">
            <thead className="bg-paper">
              <tr>
                <th className="text-left font-heading font-semibold px-4 py-3 text-ink border-b border-border sticky left-0 bg-paper z-10">Capability</th>
                {["Explorer", "Solo", "Apprentice", "Creator", "Founder"].map((t) => (
                  <th key={t} className="text-center font-heading font-semibold px-4 py-3 text-ink border-b border-border min-w-[120px]">{t}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MATRIX_ROWS.map((row, ri) => (
                <tr key={ri} className={ri % 2 ? "bg-paper/50" : ""}>
                  <td className="px-4 py-3 border-b border-border/50 text-ink2 sticky left-0 bg-inherit z-10">
                    <div className="flex items-center gap-2">
                      {row.icon && <span className="text-marigold">{row.icon}</span>}
                      <span className={row.section ? "font-bold text-ink uppercase text-[10px] tracking-widest" : ""}>{row.label}</span>
                    </div>
                  </td>
                  {row.values.map((v, i) => (
                    <td key={i} className="text-center px-4 py-3 border-b border-border/50 text-ink">
                      {v === true ? <Check className="w-4 h-4 mx-auto text-teal" /> :
                       v === false ? <span className="text-ink2/40">—</span> :
                       <span className="text-xs">{v}</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-ink2 text-center mt-4">Kailash and Knuth are in beta. Free tier gets 3 build configs / month; paid tiers scale with your monthly prompt allowance.</p>
      </section>

      {/* Institution / Enterprise */}
      <div className="card-soft p-8 md:p-10 mb-20 grid lg:grid-cols-3 gap-8 items-center" data-testid="tier-institution">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-ink text-cream flex items-center justify-center"><Building2 className="w-5 h-5" /></div>
            <div className="text-xs text-ink2 uppercase tracking-widest font-semibold">Enterprise · School</div>
          </div>
          <h3 className="font-heading text-3xl md:text-4xl font-semibold mb-2">Institution</h3>
          <p className="text-ink2 mb-5 max-w-xl">For schools, colleges, coding academies, and government programs. From 100 to 10,000 student licenses, teacher dashboards, custom learning paths, and bulk pop-up book orders. White-label optional.</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink2">
            <span>· 100–10,000 student licenses</span>
            <span>· Teacher dashboard</span>
            <span>· Progress tracking</span>
            <span>· Offline + classroom kits</span>
            <span>· Certification programs</span>
          </div>
        </div>
        <div className="text-center lg:text-right">
          <div className="text-xs text-ink2 uppercase tracking-widest font-semibold mb-1">Custom</div>
          <div className="font-heading text-3xl font-semibold mb-1">₹3L – ₹60L+</div>
          <div className="text-sm text-ink2 mb-5">per year</div>
          <a href="mailto:hello@durgaprogrammingadventures.com?subject=Institution%20enquiry" className="btn-primary" data-testid="institution-cta">
            Talk to us <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      {/* Bundles */}
      <section className="mb-20" data-testid="bundles-section">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="label-eyebrow mb-2 inline-flex items-center gap-2"><Package className="w-3.5 h-3.5" /> Yearly Bundles</div>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold">Save more, ship more.</h2>
            <p className="text-ink2 mt-2 text-sm max-w-xl">Each bundle locks in a year of premium tier + every add-on at ~30% off the à-la-carte price.</p>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {BUNDLES.map(b => (
            <div key={b.id} className={`rounded-2xl p-6 border-2 flex flex-col ${b.popular ? "bg-ink text-cream border-ink" : "bg-paper border-border"}`} data-testid={`bundle-${b.id}`}>
              <div className={`text-xs uppercase tracking-widest font-semibold mb-2 ${b.popular ? "text-saffron" : "text-marigold"}`}>Bundle{b.popular && " · Best value"}</div>
              <h3 className="font-heading text-2xl font-semibold mb-2">{b.name}</h3>
              <p className={`text-sm mb-4 flex-grow ${b.popular ? "text-cream/75" : "text-ink2"}`}>{b.blurb}</p>
              <div className="font-heading text-3xl font-semibold mb-4">₹{b.price_inr.toLocaleString("en-IN")}<span className={`text-sm font-medium ml-1 ${b.popular ? "text-cream/70" : "text-ink2"}`}>/year</span></div>
              <button
                type="button"
                disabled={buying === b.id}
                onClick={() => startCheckout(b.id, b.name)}
                className={`w-full rounded-full px-5 py-2.5 font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60 transition ${b.popular ? "bg-saffron text-ink hover:-translate-y-0.5" : "border-2 border-ink text-ink hover:bg-ink hover:text-cream"}`}
                data-testid={`bundle-cta-${b.id}`}
              >
                {buying === b.id ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <>Buy with PayPal</>}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Add-ons */}
      <section className="mb-20" data-testid="addons-section">
        <div className="label-eyebrow mb-2 inline-flex items-center gap-2"><Plug className="w-3.5 h-3.5" /> Add-ons</div>
        <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-2">Plug in extra power.</h2>
        <p className="text-ink2 mb-6 max-w-xl text-sm">No tier upgrade required — top up prompts, marketing renders, or full launch services on demand.</p>
        <div className="grid md:grid-cols-3 gap-5">
          {ADDONS.map(a => (
            <div key={a.id} className="card-soft p-6 flex flex-col" data-testid={`addon-${a.id}`}>
              <h3 className="font-heading text-lg font-semibold mb-1.5">{a.name}</h3>
              <p className="text-sm text-ink2 mb-4 flex-grow">{a.blurb}</p>
              <div className="font-heading text-xl font-semibold text-marigold mb-4">{a.price}</div>
              <button
                type="button"
                disabled={buying === a.id}
                onClick={() => startCheckout(a.id, a.name)}
                className="w-full rounded-full px-5 py-2.5 font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60 border-2 border-ink text-ink hover:bg-ink hover:text-cream transition"
                data-testid={`addon-cta-${a.id}`}
              >
                {buying === a.id ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <>Buy with PayPal</>}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Books pricing */}
      <section className="card-soft p-8 md:p-10" data-testid="book-pricing-section">
        <div className="grid lg:grid-cols-3 gap-8">
          <div>
            <div className="label-eyebrow mb-2 inline-flex items-center gap-2"><BookOpen className="w-3.5 h-3.5" /> Pop-Up Books</div>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-3">Computer science you can touch.</h2>
            <p className="text-ink2 mb-4">Digital interactive books, hardcover pop-ups, and bulk school kits.</p>
            <Link to="/books" className="btn-outline-ink text-sm py-2">Browse the bookshop</Link>
          </div>
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-3">
            {BOOK_PRICING.map((b, i) => (
              <div key={i} className="flex items-center justify-between bg-paper rounded-xl px-4 py-3 border border-border">
                <span className="text-sm text-ink">{b.label}</span>
                <span className="font-heading font-semibold text-ink">{b.price}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="mt-16 text-center text-ink2 text-sm">
        Need something custom? <a href="mailto:hello@durgaprogrammingadventures.com" className="text-marigold font-medium">hello@durgaprogrammingadventures.com</a>.
      </div>
    </div>
  );
}
